/**
 * Více menu — overflow account sections (bottom nav retail shell).
 *
 * Overflow membership SSOT: `buildAccountOverflowMenuItems` (Oblíbené when
 * `VITE_CUSTOMER_FAVORITES_NAV_ENABLED`; Obchody `/shops` when
 * `VITE_CUSTOMER_SHOPS_NAV_ENABLED` — More + sidebar; excluded from bar composition). Gift strip and
 * find-order flags come from `useCustomerNavModel` so bar and drawer agree
 * (including R1 gift strip and pickup entitlement).
 * Icons match bottom-nav glyphs so overflow items keep the same picture affordance.
 * Legacy in-page hub chooser ("What would you like to do?") is removed — destinations live here.
 */

import { useEffect, useMemo, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_RETAIL_EVENTS } from 'pi-kiosk-shared/analyticsEvents';
import {
  isMoreRowActive,
  isMoreRoute,
  peekMemoryMoreDestination,
  resolveMoreRowActive,
  setMemoryMoreDestination,
} from './moreRouteUtils.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useGiftSurfaceVisibility } from '../navigation/useGiftSurfaceVisibility.js';
import { useCustomerNavModel } from '../navigation/useCustomerNavModel.js';
import { useCustomerFeatureFlags } from '../flags/useCustomerFeatureFlags.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import { CUSTOMER_CONTENT_STANDARD_CLASS, CUSTOMER_TAB_CONTENT_CLASS } from '../../app/customerTopBarStyles.js';
import { Card } from '../../shared/ui/Card.js';
import { buildAccountOverflowMenuItems } from '../account/accountMenuItems.js';
import {
  PLATFORM_ACCOUNT_SHOP_PATH,
  PLATFORM_FAVORITES_PATH,
  PLATFORM_ORGS_PATH,
  PLATFORM_SHOPS_PATH,
  resolveShopHref,
} from '../shop/resolveShopHref.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { PROMOTED_NAV_ICON_REGISTRY } from '../navigation/customerNavIcons.js';
import {
  HomeIcon,
  ReceiptIcon,
  ShoppingBagIcon,
  ShopsDirectoryIcon,
} from '../../shared/ui/BottomNavIcons.js';
import { resolveAccountNavTenantCode } from '../../shared/tenant/customerApiTenant.js';
import {
  clearPostAuthOrdersIntent,
  peekInMemoryReturnTarget,
  peekPlatformReturnTarget,
  peekReturnTarget,
  savePlatformReturnTarget,
} from '../../shared/session/returnTarget.js';
import {
  BOTTOM_NAV_AUTH_INTENT_STATE_KEY,
  isBottomNavAuthChromePath,
  setBottomNavMemoryAuthIntent,
} from '../../app/bottomNavActiveTab.js';
import { useMoreShellOptional } from '../../app/MoreShellContext.js';

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface MoreMenuRow {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  readonly testId?: string;
  readonly to?: string;
  readonly onClick?: () => void;
  readonly Icon?: NavIcon;
  readonly active?: boolean;
}

/** Stable overflow row selectors — prefer over localized link names in tests. */
export function resolveMoreRowTestId(to: string): string | undefined {
  if (to === PLATFORM_SHOPS_PATH || to.endsWith('/shops')) {
    return 'customer-more-shops';
  }
  if (to === PLATFORM_ORGS_PATH || to.endsWith('/orgs')) {
    return 'customer-more-orgs';
  }
  if (
    to === PLATFORM_FAVORITES_PATH ||
    to.endsWith('/favorites') ||
    to === PLATFORM_ACCOUNT_SHOP_PATH ||
    to.endsWith('/account/shop')
  ) {
    return 'customer-more-favorites';
  }
  if (to.endsWith('/orders/find')) {
    return 'customer-more-find-order';
  }
  if (/\/account\/donate(?:\/|$)/.test(to)) {
    return 'customer-more-donate';
  }
  if (/\/account\/recurring-donations(?:\/|$)/.test(to)) {
    return 'customer-more-recurring';
  }
  if (/\/account\/donations(?:\/|$)/.test(to)) {
    return 'customer-more-donations';
  }
  if (/\/account\/?$/.test(to)) {
    return 'customer-more-account-home';
  }
  return undefined;
}

function resolveMoreRowIcon(to: string): NavIcon | undefined {
  if (to === '/' || to === '') {
    return HomeIcon;
  }
  if (to === PLATFORM_SHOPS_PATH || to.endsWith('/shops')) {
    return ShopsDirectoryIcon;
  }
  // Exact account home (Kupóny) — coupons / promo events hub.
  if (/\/account\/?$/.test(to)) {
    return PROMOTED_NAV_ICON_REGISTRY.overview.Icon;
  }
  if (to.includes('/account/shop') || to.endsWith('/shop')) {
    return ShoppingBagIcon;
  }
  if (to.includes('/account/payments') || to.includes('/orders')) {
    return ReceiptIcon;
  }
  if (to.includes('/donate') && !to.includes('recurring') && !to.includes('donations')) {
    return PROMOTED_NAV_ICON_REGISTRY.donate.Icon;
  }
  if (to.includes('/recurring-donations')) {
    return PROMOTED_NAV_ICON_REGISTRY.recurring.Icon;
  }
  if (to.includes('/account/donations')) {
    return PROMOTED_NAV_ICON_REGISTRY['my-donations'].Icon;
  }
  return undefined;
}

function isProtectedMoreDestination(to: string): boolean {
  return (
    /\/account(?:\/|$)/.test(to) ||
    to.includes('/orders/find') ||
    /\/orders(?:\/|$)/.test(to)
  );
}

function moreRowClassName(active: boolean): string {
  const base =
    'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]';
  if (active) {
    return [
      base,
      'border-[var(--color-accent)]',
      'bg-[var(--color-accent)]',
      'text-[var(--color-accent-foreground)]',
      'font-semibold',
      'shadow-[0_1px_2px_rgba(0,0,0,0.12)]',
    ].join(' ');
  }
  return [
    base,
    'border-[var(--color-border)]',
    'bg-[var(--color-surface)]',
    'hover:bg-[var(--color-surface-elevated)]',
  ].join(' ');
}

function MoreMenuList({ rows }: { rows: readonly MoreMenuRow[] }): JSX.Element {
  return (
    <ul className="flex flex-col gap-2" data-testid="customer-more-menu">
      {rows.map((row) => {
        const active = row.active === true;
        const rowClass = moreRowClassName(active);
        const Icon = row.Icon;
        const body = (
          <>
            {Icon !== undefined ? (
              <Icon
                className={[
                  'mt-0.5 h-5 w-5 shrink-0',
                  active ? 'text-[var(--color-accent-foreground)]' : 'text-[var(--color-on-surface)]',
                ].join(' ')}
              />
            ) : null}
            <span className="flex min-w-0 flex-col">
              <span
                className={[
                  'text-sm font-medium',
                  active ? 'text-[var(--color-accent-foreground)]' : 'text-[var(--color-on-surface)]',
                ].join(' ')}
              >
                {row.label}
              </span>
              {row.description !== undefined ? (
                <span
                  className={[
                    'mt-1 text-xs',
                    active
                      ? 'text-[var(--color-accent-foreground)] opacity-90'
                      : 'text-[var(--color-on-surface-muted)]',
                  ].join(' ')}
                >
                  {row.description}
                </span>
              ) : null}
            </span>
          </>
        );

        if (row.to !== undefined) {
          return (
            <li key={row.key}>
              <Link
                to={row.to}
                className={rowClass}
                data-testid={row.testId}
                aria-current={active ? 'page' : undefined}
                onClick={row.onClick}
              >
                {body}
              </Link>
            </li>
          );
        }

        return (
          <li key={row.key}>
            <button
              type="button"
              className={rowClass}
              data-testid={row.testId}
              aria-current={active ? 'page' : undefined}
              onClick={row.onClick}
            >
              {body}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface MoreMenuPanelProps {
  /** Kept for drawer API compatibility; overflow menu is always used when authed. */
  readonly compact?: boolean;
}

export function MoreMenuPanel(_props: MoreMenuPanelProps): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const location = useLocation();
  const navigate = useNavigate();
  const moreShell = useMoreShellOptional();
  const { session } =
    useCustomerSession();
  const gift = useGiftSurfaceVisibility();
  const { giftForOverflow, showFindOrder, showKupony, showShops, showFavorites } =
    useCustomerNavModel();
  const { pollRevision } = useCustomerFeatureFlags();
  const analytics = useCustomerAnalytics();
  const lastImpressionRevisionRef = useRef<number | null>(null);
  const [optimisticActiveTo, setOptimisticActiveTo] = useState<string | null>(null);
  // Wave D — overflow destinations are platform `/account/**` (jwt unused for hrefs).
  const menuTenant = resolveAccountNavTenantCode(tenantCode);

  const favoritesHref = resolveShopHref(undefined, session);
  // showFindOrder + giftForOverflow from useCustomerNavModel (parity with bar).
  // Shop/orders are always-on bottom-nav primaries (reserved out of Více).
  const signedInChrome = isGloballySignedInChrome({ session });

  // Keep the chosen overflow row lit on auth chrome / /more hub via memory.
  // On a live overflow page, route matching alone owns selection (no dual-active).
  const memoryMoreDestination = peekMemoryMoreDestination();
  const pendingMoreDestination = ((): string | null => {
    if (isBottomNavAuthChromePath(location.pathname, tenantCode)) {
      return (
        peekInMemoryReturnTarget() ??
        peekReturnTarget({ tenantCode: menuTenant }) ??
        peekPlatformReturnTarget() ??
        memoryMoreDestination
      );
    }
    if (isMoreRoute(location.pathname, tenantCode)) {
      return memoryMoreDestination;
    }
    return null;
  })();

  // Drop optimistic selection once the route (or auth return target) owns it.
  if (
    optimisticActiveTo !== null &&
    (isMoreRowActive(location.pathname, optimisticActiveTo) ||
      (pendingMoreDestination !== null &&
        isMoreRowActive(pendingMoreDestination, optimisticActiveTo)))
  ) {
    setOptimisticActiveTo(null);
  }

  useEffect(() => {
    if (!gift.canShowGiftSurfaces || pollRevision === null) {
      return;
    }
    if (lastImpressionRevisionRef.current === pollRevision) {
      return;
    }
    lastImpressionRevisionRef.current = pollRevision;
    analytics.track({
      eventName: ANALYTICS_RETAIL_EVENTS.GIFT_SURFACE_IMPRESSION,
      flow: 'RETAIL',
      screenName: 'more',
      metadata: {
        revisionHash: String(pollRevision),
        donateVisible: gift.donateVisible,
        recurringVisible: gift.recurringVisible,
        myDonationsVisible: gift.myDonationsVisible,
      },
    });
  }, [
    analytics,
    gift.canShowGiftSurfaces,
    gift.donateVisible,
    gift.myDonationsVisible,
    gift.recurringVisible,
    pollRevision,
  ]);

  const overflowRows: readonly MoreMenuRow[] = useMemo(
    () =>
      buildAccountOverflowMenuItems(
        menuTenant,
        t,
        {
          donate: giftForOverflow.donateVisible,
          recurring: giftForOverflow.recurringVisible,
          myDonations: giftForOverflow.myDonationsVisible,
        },
        { showFindOrder, showKupony, showShops, showFavorites, favoritesHref },
      ).map((item) => ({
        key: item.to,
        label: item.label,
        description: item.description,
        to: item.to,
        testId: resolveMoreRowTestId(item.to),
        Icon: resolveMoreRowIcon(item.to),
        active: resolveMoreRowActive({
          pathname: location.pathname,
          to: item.to,
          pendingPath: pendingMoreDestination,
          optimisticPath: optimisticActiveTo,
        }),
      })),
    [
      giftForOverflow.donateVisible,
      giftForOverflow.myDonationsVisible,
      giftForOverflow.recurringVisible,
      location.pathname,
      menuTenant,
      optimisticActiveTo,
      pendingMoreDestination,
      showFindOrder,
      showKupony,
      showShops,
      showFavorites,
      favoritesHref,
      t,
    ],
  );

  const menuRows: readonly MoreMenuRow[] = useMemo(() => {
    // Wave D — guest park is always platform `/sign-in`.
    const signInHref = '/sign-in';
    const closeDrawer = (): void => {
      moreShell?.closeMore();
    };
    const markOptimistic = (destination: string): void => {
      setMemoryMoreDestination(destination);
      setOptimisticActiveTo(destination);
    };
    const redirectToSignInForDestination = (destination: string): void => {
      // Persist the *menu destination*, not the current URL (often /sign-in or
      // /more). Otherwise a prior Moje objednávky return-target sticks and the
      // bottom-nav marker stays on orders instead of Více.
      markOptimistic(destination);
      // Platform `/account/**` destinations — always park on platform allowlist.
      savePlatformReturnTarget(destination);
      clearPostAuthOrdersIntent();
      setBottomNavMemoryAuthIntent('more');
      const alreadyOnSignIn =
        location.pathname === signInHref ||
        location.pathname.endsWith('/sign-in') ||
        location.pathname === '/sign-in';
      navigate(signInHref, {
        replace: alreadyOnSignIn,
        state: { [BOTTOM_NAV_AUTH_INTENT_STATE_KEY]: 'more' },
      });
      // Keep drawer open until pathname lands so Více stays lit (iOS dual-active).
      // Same-URL sign-in replace would not remount — close explicitly.
      if (alreadyOnSignIn) {
        closeDrawer();
      }
    };

    const findOrderPath = '/orders/find';
    const findOrderRow = overflowRows.find((row) => row.to === findOrderPath);

    // Objednávky is a bottom-nav primary — never duplicate here.
    // HUX-01: Org / Settings / Profile leave root Home first-row → More secondary.
    // Near the end: … → Najít objednávku → Org · Settings · Profile sheets.
    const coreRows = overflowRows.filter((row) => row.to !== findOrderPath);

    const chromeSecondaryRows: MoreMenuRow[] = [
      {
        key: 'chrome-orgs',
        label: t('nav.orgs'),
        to: PLATFORM_ORGS_PATH,
        testId: 'customer-more-orgs',
      },
      {
        key: 'chrome-settings',
        label: t('chrome.settings.title', { defaultValue: t('nav.settings') }),
        description: t('chrome.settings.description'),
        testId: 'customer-more-settings',
        onClick: () => {
          moreShell?.openSettingsSheet();
        },
      },
      {
        key: 'chrome-profile',
        label: t('more.myAccount'),
        testId: 'customer-more-profile',
        onClick: () => {
          moreShell?.openProfileSheet();
        },
      },
    ];

    const orderedRows: MoreMenuRow[] = [
      ...coreRows,
      ...(findOrderRow !== undefined ? [findOrderRow] : []),
      ...chromeSecondaryRows,
    ];

    return orderedRows.map((row) => {
      // iOS dual-active: do not close on navigate — shell closes after pathname
      // changes so Více stays selected and the row can light before dismiss.
      // Only close when already on this destination (no route change).
      const closeIfAlreadyHere = (): void => {
        if (row.to !== undefined && isMoreRowActive(location.pathname, row.to)) {
          closeDrawer();
        }
      };

      if (!signedInChrome && row.to !== undefined && isProtectedMoreDestination(row.to)) {
        const destination = row.to;
        return {
          ...row,
          to: undefined,
          onClick: () => {
            redirectToSignInForDestination(destination);
          },
        };
      }

      if (row.to !== undefined) {
        const destination = row.to;
        return {
          ...row,
          onClick: () => {
            markOptimistic(destination);
            closeIfAlreadyHere();
          },
        };
      }

      return row;
    });
  }, [
    location.pathname,
    moreShell,
    navigate,
    overflowRows,
    signedInChrome,
    t,
  ]);

  if (!signedInChrome) {
    return (
      <div className={CUSTOMER_TAB_CONTENT_CLASS} data-testid="customer-more-guest">
        <Card padded>
          <MoreMenuList rows={menuRows} />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="customer-more-overflow">
      <Card padded>
        <MoreMenuList rows={menuRows} />
      </Card>
    </div>
  );
}

/** Route-mounted More page. */
export function MorePage(): JSX.Element {
  const { t } = useTranslation('customer');

  return (
    <div className={`${CUSTOMER_TAB_CONTENT_CLASS} ${CUSTOMER_CONTENT_STANDARD_CLASS}`}>
      <h1 className="text-lg font-semibold text-[var(--color-on-surface)]">{t('more.title')}</h1>
      <MoreMenuPanel />
    </div>
  );
}
